import { createContext, PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react';

import { getSession, removeSession, setSession } from '../common';
import { addDayOff, getDaysOff, removeDayOff } from '../settings/api';
import { type DayOff, manualDayOff } from '../settings/models';
import { useSettings } from '../settings/SettingsProvider';
import { useActingAsState } from './actingAsState';
import { useCurrentUserState } from './currentUserState';
import type { Caller } from './models';

type CallerStatus = 'loading' | 'ready' | 'error';

export type CurrentUserContextValue = {
    status: CallerStatus;
    // Whose calendar the app shows: the actor, or the member acted for.
    user: Caller | null;
    // The signed-in member who owns the PAT.
    actor: Caller | null;
    isActing: boolean;
    // Email to send as `onBehalfOf` on writes while acting.
    onBehalfOf: string | undefined;
    // Flagged in the database and not already acting — one level only.
    canActOnBehalf: boolean;
    startActingAs: (memberId: string) => void;
    stopActing: () => void;
    daysOff: Map<string, DayOff>;
    daysOffVersion: number;
    canEditDaysOff: boolean;
    toggleDayOff: (date: string) => Promise<void>;
    refreshDaysOff: () => Promise<void>;
    error: string | null;
};

export const CurrentUserContext = createContext<CurrentUserContextValue | undefined>(undefined);

// Per tab, so a new tab always starts as yourself.
const ACTING_AS_KEY = 'nuxni-time-tracker:acting-as';

export const CurrentUserProvider: React.FC<PropsWithChildren> = ({ children }) => {
    const { configured, settings } = useSettings();
    const loaded = useCurrentUserState(configured, settings.org, settings.pat);
    const actor = loaded.user;

    const [actingAsId, setActingAsId] = useState<string | null>(() => getSession<string>(ACTING_AS_KEY));
    // Only a flagged actor acts, and never as themselves.
    const validActingAsId = actor?.canActOnBehalf && actingAsId && actingAsId !== actor.id ? actingAsId : null;
    const acting = useActingAsState(validActingAsId);
    const isActing = validActingAsId !== null;
    const user = isActing ? acting.user : actor;

    const [daysOff, setDaysOffState] = useState<Map<string, DayOff>>(new Map());
    const [daysOffVersion, setDaysOffVersion] = useState(0);
    const [localError, setLocalError] = useState<string | null>(null);
    const [actingError, setActingError] = useState<string | null>(null);

    const sourceDaysOff = isActing ? acting.daysOff : loaded.daysOff;
    useEffect(() => {
        setDaysOffState(sourceDaysOff);
        setLocalError(null);
    }, [sourceDaysOff]);

    const stopActing = useCallback(() => {
        removeSession(ACTING_AS_KEY);
        setActingAsId(null);
    }, []);

    const startActingAs = useCallback(
        (memberId: string) => {
            if (!actor?.canActOnBehalf || isActing || memberId === actor.id) {
                return;
            }
            setActingError(null);
            setSession(ACTING_AS_KEY, memberId);
            setActingAsId(memberId);
        },
        [actor, isActing],
    );

    // A stored id this actor may not use ends acting.
    useEffect(() => {
        if (loaded.status === 'ready' && actingAsId && !validActingAsId) {
            stopActing();
        }
    }, [loaded.status, actingAsId, validActingAsId, stopActing]);

    useEffect(() => {
        if (acting.status === 'error') {
            setActingError(`Could not act on behalf: ${acting.error ?? 'member not found'}`);
            stopActing();
        }
    }, [acting.status, acting.error, stopActing]);

    const toggleDayOff = useCallback(
        async (date: string) => {
            if (!user) {
                return;
            }
            const isAdding = !daysOff.has(date);
            const next = new Map(daysOff);
            if (isAdding) {
                next.set(date, manualDayOff(date));
            } else {
                next.delete(date);
            }
            // Optimistic — the writes are idempotent on the server side, so
            // a refresh re-syncs cleanly if anything is off.
            setDaysOffState(next);
            try {
                if (isAdding) {
                    await addDayOff(user.id, date);
                } else {
                    await removeDayOff(user.id, date);
                }
                setDaysOffVersion((v) => v + 1);
                setLocalError(null);
            } catch (e) {
                // Roll back the optimistic flip on failure.
                setDaysOffState(daysOff);
                setLocalError(e instanceof Error ? e.message : 'Failed to update days off.');
            }
        },
        [user, daysOff],
    );

    const refreshDaysOff = useCallback(async () => {
        if (!user) {
            return;
        }
        try {
            const { items } = await getDaysOff(user.id);
            setDaysOffState(new Map(items.map((d) => [d.date, d])));
            setDaysOffVersion((v) => v + 1);
            setLocalError(null);
        } catch (e) {
            setLocalError(e instanceof Error ? e.message : 'Failed to refresh days off.');
        }
    }, [user]);

    const actingReady = !isActing || acting.status === 'ready';
    const status: CallerStatus = loaded.status === 'ready' ? (actingReady ? 'ready' : 'loading') : loaded.status;

    const value = useMemo<CurrentUserContextValue>(
        () => ({
            status,
            user,
            actor,
            isActing,
            onBehalfOf: isActing ? user?.email : undefined,
            canActOnBehalf: Boolean(actor?.canActOnBehalf) && !isActing,
            startActingAs,
            stopActing,
            daysOff,
            daysOffVersion,
            canEditDaysOff: user !== null,
            toggleDayOff,
            refreshDaysOff,
            error: localError ?? actingError ?? loaded.error,
        }),
        [
            status,
            user,
            actor,
            isActing,
            startActingAs,
            stopActing,
            daysOff,
            daysOffVersion,
            toggleDayOff,
            refreshDaysOff,
            localError,
            actingError,
            loaded.error,
        ],
    );

    return <CurrentUserContext.Provider value={value}>{children}</CurrentUserContext.Provider>;
};
